import React, { useEffect, useRef, useState } from 'react';

import { createChart } from 'lightweight-charts';
import Card from '../../common/Card/Card';
import './TechnicalIndicators.css';
import { useStockData } from '../../../hooks/useStockData';


//Dummy data for technical indicators

const TechnicalIndicators = ({ symbol, onChartClick, analysis, showClickReset, onReadMore }) => {
  // Fetch live price + history and auto-refresh every 60s
  const {
    stockData,
    isLoading,
    error
  } = useStockData(symbol, {
    autoFetch: true,
    fetchInterval: 60000,
    dataTypes: ['history', 'price']
  });

  // Track loading state for symbol changes
  const [isSymbolLoading, setIsSymbolLoading] = useState(false);
  const [currentSymbol, setCurrentSymbol] = useState(symbol);
  // Helper function for robust date formatting
  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return '—';
      
      let date;
      if (typeof timestamp === 'number') {
        // Check if it's already in milliseconds or seconds
        if (timestamp > 2000000000) {
          // Already in milliseconds
          date = new Date(timestamp);
        } else {
          // In seconds, convert to milliseconds
          date = new Date(timestamp * 1000);
        }
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp && typeof timestamp === 'object' && typeof timestamp.year === 'number') {
        // Business day object from Lightweight Charts
        date = new Date(Date.UTC(timestamp.year, (timestamp.month || 1) - 1, timestamp.day || 1));
      } else {
        return '—';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '—';
      }
      
      // Format as MM/DD/YYYY
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return '—';
    }
  };

  const [visibleCharts, setVisibleCharts] = useState({
    price: true,
    rsi: true,
    macd: true,
    volume: true
  });

  const chartRefs = {
    price: useRef(null),
    rsi: useRef(null),
    macd: useRef(null),
    volume: useRef(null)
  };

  const chartInstances = useRef({});
  const seriesRefs = useRef({}); // hold primary series per chartType for markers
  const lastPriceLineRefs = useRef({}); // track last price line per chartType
  const dataRefs = useRef({}); // store data per chartType for lookup on click

  // Loading overlay component
  const LoadingOverlay = ({ message = "Loading..." }) => (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(255, 255, 255, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      borderRadius: '8px'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #e5e7eb',
          borderTop: '3px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div style={{
          color: '#6b7280',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {message}
        </div>
      </div>
    </div>
  );

  // AnalysisCard component with read more functionality
  const AnalysisCard = ({ title, content, height = 300 }) => {
    const contentRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      const overflowing = el.scrollHeight > el.clientHeight + 1;
      setIsOverflowing(overflowing);
    }, [content, height]);

    return (
      <Card title={title} style={{ width: 300, height, marginBottom: 0 }}>
        <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
          <div ref={contentRef} style={{ height: '100%', overflow: 'hidden', color: '#6b7280', padding: 16 }}>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{content}</p>
          </div>
          {isOverflowing && (
            <>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 54,
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.9))',
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  pointerEvents: 'none',
                }}
              />
              <button
                onClick={() => onReadMore && onReadMore({ title, content })}
                style={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  fontSize: 12,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  color: '#111827',
                  cursor: 'pointer',
                }}
                aria-label={`Read more: ${title}`}
              >
                Read more
              </button>
            </>
          )}
        </div>
      </Card>
    );
  };

  // Mock data generation
  const generateMockData = () => {
    const data = [];
    let basePrice = 150;
    let baseVolume = 1000000;

    for (let i = 0; i < 100; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (100 - i));
      
      const change = (Math.random() - 0.5) * 10;
      const open = basePrice;
      const close = basePrice + change;
      const high = Math.max(open, close) + Math.random() * 5;
      const low = Math.min(open, close) - Math.random() * 5;
      
      data.push({
        time: Math.floor(date.getTime() / 1000),
        open: open,
        high: high,
        low: low,
        close: close,
        volume: baseVolume + Math.random() * 500000
      });

      basePrice = close;
      baseVolume = baseVolume + (Math.random() - 0.5) * 200000;
    }
    return data;
  };

  // Generate technical indicators (mock data)
  const generateTechnicalData = (priceData) => {
    const rsiData = [];
    const macdData = [];
    const volumeData = [];

    priceData.forEach((item, index) => {
      // Mock RSI (30-70 range)
      const rsi = 30 + Math.random() * 40;
      rsiData.push({
        time: item.time,
        value: rsi
      });

      // Mock MACD
      const macd = (Math.random() - 0.5) * 10;
      const signal = macd * 0.8;
      const histogram = macd - signal;
      macdData.push({
        time: item.time,
        macd: macd,
        signal: signal,
        histogram: histogram
      });

      // Volume data
      volumeData.push({
        time: item.time,
        value: item.volume,
        color: item.close >= item.open ? '#10b981' : '#ef4444'
      });
    });

    return { rsiData, macdData, volumeData };
  };

  const initializeChart = (chartType, containerRef) => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 200,
      layout: {
        background: { color: '#ffffff' },
        textColor: '#1e293b',
      },
      grid: {
        vertLines: { color: '#e2e8f0' },
        horzLines: { color: '#e2e8f0' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: '#e2e8f0',
      },
      timeScale: {
        borderColor: '#e2e8f0',
        timeVisible: true,
        secondsVisible: false,
      },
    });

    chartInstances.current[chartType] = chart;
    // Subscribe to click events if provided
    if (onChartClick) {
      chart.subscribeClick((param) => {
        try {
          const base = {
            type: chartType,
            chartGroup: 'indicators',
            symbol,
            event: 'click',
            time: param?.time ?? null,
            logical: param?.logical ?? null,
            point: param?.point ? { x: param.point.x, y: param.point.y } : null,
          };

          // Derive clicked value using the primary series' price scale
          let value = null;
          try {
            const series = seriesRefs.current[chartType];
            if (param?.point && series && typeof series.priceScale === 'function') {
              const ps = series.priceScale();
              if (ps && typeof ps.coordinateToPrice === 'function') {
                const v = ps.coordinateToPrice(param.point.y);
                if (typeof v === 'number' && !Number.isNaN(v)) value = v;
              }
            }
          } catch (_) {}

          const out = { ...base, value };

          // Enrich with series-specific data
          if (chartType === 'price' && base.time && Array.isArray(dataRefs.current.price)) {
            const bar = dataRefs.current.price.find(d => d.time === base.time);
            console.log('Price click - found bar:', bar, 'for time:', base.time);
            if (bar) out.ohlc = { open: bar.open, high: bar.high, low: bar.low, close: bar.close };
          }
          if (chartType === 'macd' && base.time && Array.isArray(dataRefs.current.macd)) {
            const row = dataRefs.current.macd.find(d => d.time === base.time);
            console.log('MACD click - found row:', row, 'for time:', base.time, 'dataRefs.macd length:', dataRefs.current.macd?.length);
            if (row) out.extras = { macd: row.macd, signal: row.signal, histogram: row.histogram };
            // Also include price data and history for MACD clicks so analysis can work
            if (Array.isArray(dataRefs.current.price)) {
              const bar = dataRefs.current.price.find(d => d.time === base.time);
              if (bar) out.ohlc = { open: bar.open, high: bar.high, low: bar.low, close: bar.close };
              out.history = dataRefs.current.price;
            }
          }
          // For RSI clicks, also include the price data at that time for analysis
          if (chartType === 'rsi' && base.time && Array.isArray(dataRefs.current.price)) {
            const bar = dataRefs.current.price.find(d => d.time === base.time);
            console.log('RSI click - found bar:', bar, 'for time:', base.time, 'dataRefs.price length:', dataRefs.current.price?.length);
            if (bar) out.ohlc = { open: bar.open, high: bar.high, low: bar.low, close: bar.close };
            // Also include history data for RSI clicks so analysis can work
            if (Array.isArray(dataRefs.current.price)) {
              out.history = dataRefs.current.price;
            }
          }
          // For Volume clicks, also include the price data at that time for analysis
          if (chartType === 'volume' && base.time && Array.isArray(dataRefs.current.price)) {
            const bar = dataRefs.current.price.find(d => d.time === base.time);
            console.log('Volume click - found bar:', bar, 'for time:', base.time, 'dataRefs.price length:', dataRefs.current.price?.length);
            if (bar) out.ohlc = { open: bar.open, high: bar.high, low: bar.low, close: bar.close };
            // Also include history data for Volume clicks so analysis can work
            if (Array.isArray(dataRefs.current.price)) {
              out.history = dataRefs.current.price;
            }
          }

          console.log('Chart click - final out object:', out);
          onChartClick(out);
        } catch (error) {
          console.error('Chart click error:', error);
        }
      });
    }
    // Local marker drop on click
    chart.subscribeClick((param) => {
      try {
        if (!param?.time) return;
        const series = seriesRefs.current[chartType];
        if (!series || !series.setMarkers) return;
        const marker = {
          time: param.time,
          position: 'inBar',
          color: '#000000',
          shape: 'circle',
          text: '',
        };
        series.setMarkers([marker]);

        // Add/replace a horizontal line at exact clicked value for this series
        if (param.point && typeof series.priceScale === 'function') {
          const ps = series.priceScale();
          if (ps && typeof ps.coordinateToPrice === 'function') {
            const value = ps.coordinateToPrice(param.point.y);
            if (typeof value === 'number' && !Number.isNaN(value)) {
              // remove previous price line for this chartType
              try {
                if (lastPriceLineRefs.current[chartType] && typeof series.removePriceLine === 'function') {
                  series.removePriceLine(lastPriceLineRefs.current[chartType]);
                }
              } catch (_) {}
              const pl = series.createPriceLine({
                price: value,
                color: '#000000',
                lineWidth: 1,
                lineStyle: 0,
                axisLabelVisible: true,
                title: ''
              });
              lastPriceLineRefs.current[chartType] = pl;
            }
          }
        }
      } catch (_) {}
    });
    return chart;
  };

  const setupPriceChart = () => {
    const chart = initializeChart('price', chartRefs.price);
    if (!chart) return;

    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#10b981',
      downColor: '#ef4444',
      borderDownColor: '#ef4444',
      borderUpColor: '#10b981',
      wickDownColor: '#ef4444',
      wickUpColor: '#10b981',
    });
    seriesRefs.current.price = candlestickSeries;

    const ma50Series = chart.addLineSeries({
      color: '#3b82f6',
      lineWidth: 1,
      title: 'MA 50',
    });

    const ma200Series = chart.addLineSeries({
      color: '#f59e0b',
      lineWidth: 1,
      title: 'MA 200',
    });

    // Store series to enable updates on data refresh
    seriesRefs.current.price = candlestickSeries;
    seriesRefs.current.ma50 = ma50Series;
    seriesRefs.current.ma200 = ma200Series;

    // If we already have data, render immediately
    try {
      const chartData = stockData?.history?.data;
      if (Array.isArray(chartData) && chartData.length > 0) {
        const candlestickData = chartData.map(item => ({
          time: item.date,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
        }));
        candlestickSeries.setData(candlestickData);
        dataRefs.current.price = candlestickData;

        // Simple moving averages
        const sma = (data, window) => {
          const out = [];
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            sum += data[i].close;
            if (i >= window) sum -= data[i - window].close;
            if (i >= window - 1) out.push({ time: data[i].time, value: sum / window });
          }
          return out;
        };

        const ma50Data = sma(candlestickData, 50);
        const ma200Data = sma(candlestickData, 200);
        ma50Series.setData(ma50Data);
        ma200Series.setData(ma200Data);
      }
    } catch (_) {}
  };

  // Update price + MA series whenever fresh history arrives
  useEffect(() => {
    try {
      const chart = chartInstances.current.price;
      const candle = seriesRefs.current.price;
      const ma50 = seriesRefs.current.ma50;
      const ma200 = seriesRefs.current.ma200;
      const chartData = stockData?.history?.data;
      
      // Debug: Log the entire stockData structure
      console.log('TechnicalIndicators - stockData:', stockData);
      console.log('TechnicalIndicators - chartData:', chartData);
      
      if (!chart || !candle || !Array.isArray(chartData) || chartData.length === 0) {
        console.warn('TechnicalIndicators - Missing chart or data:', { chart: !!chart, candle: !!candle, chartData: chartData });
        return;
      }
      
      // Debug: Log data structure to understand format
      if (chartData.length > 0) {
        console.log('Chart data sample:', chartData[0]);
        console.log('Chart data length:', chartData.length);
      }

      const candlestickData = chartData.map(item => {
        const open = Number(item.open) || Number(item.o) || 0;
        const high = Number(item.high) || Number(item.h) || 0;
        const low = Number(item.low) || Number(item.l) || 0;
        const close = Number(item.close) || Number(item.c) || 0;
        
        // Validate data - skip invalid entries
        if (!open || !high || !low || !close || isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
          console.warn('Invalid OHLC data:', item);
          return null;
        }
        
        return {
          time: item.date || item.time || item.t,
          open,
          high,
          low,
          close,
        };
      }).filter(Boolean); // Remove null entries
      candle.setData(candlestickData);
      dataRefs.current.price = candlestickData;

      const sma = (data, window) => {
        const out = [];
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          sum += data[i].close;
          if (i >= window) sum -= data[i - window].close;
          if (i >= window - 1) out.push({ time: data[i].time, value: sum / window });
        }
        return out;
      };
      // Only calculate MAs if we have enough data
      if (ma50 && candlestickData.length >= 50) ma50.setData(sma(candlestickData, 50));
      if (ma200 && candlestickData.length >= 200) ma200.setData(sma(candlestickData, 200));
    } catch (_) {}
  }, [stockData?.history?.data]);

  const setupRSIChart = () => {
    const chart = initializeChart('rsi', chartRefs.rsi);
    if (!chart) return;

    const rsiSeries = chart.addLineSeries({
      color: '#8b5cf6',
      lineWidth: 2,
      title: 'RSI',
    });
    seriesRefs.current.rsi = rsiSeries;

    const overboughtLine = chart.addLineSeries({
      color: '#ef4444',
      lineWidth: 1,
      lineStyle: 2,
      title: 'Overbought (70)',
    });

    const oversoldLine = chart.addLineSeries({
      color: '#10b981',
      lineWidth: 1,
      lineStyle: 2,
      title: 'Oversold (30)',
    });

    // If history exists, plot RSI immediately
    try {
      const chartData = stockData?.history?.data;
      if (Array.isArray(chartData) && chartData.length > 0) {
        const closes = chartData.map(x => Number(x.close) || Number(x.c) || 0);
        const times = chartData.map(x => x.date || x.time || x.t);
        const rsiPoints = computeRSI(times, closes, 14);
        rsiSeries.setData(rsiPoints);
        dataRefs.current.rsi = rsiPoints;
        overboughtLine.setData(times.map(t => ({ time: t, value: 70 })));
        oversoldLine.setData(times.map(t => ({ time: t, value: 30 })));
      }
    } catch (_) {}
  };

  const setupMACDChart = () => {
    const chart = initializeChart('macd', chartRefs.macd);
    if (!chart) return;

    const macdSeries = chart.addLineSeries({
      color: '#3b82f6',
      lineWidth: 2,
      title: 'MACD',
    });

    const signalSeries = chart.addLineSeries({
      color: '#f59e0b',
      lineWidth: 2,
      title: 'Signal',
    });

    const histogramSeries = chart.addHistogramSeries({
      color: '#10b981',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '',
      scaleMargins: {
        top: 0.8,
        bottom: 0,
      },
    });
    seriesRefs.current.macd = macdSeries;
    seriesRefs.current.macdSignal = signalSeries;
    seriesRefs.current.macdHist = histogramSeries;

    // If history exists, plot MACD immediately
    try {
      const chartData = stockData?.history?.data;
      if (Array.isArray(chartData) && chartData.length > 0) {
        const closes = chartData.map(x => Number(x.close) || Number(x.c) || 0);
        const times = chartData.map(x => x.date || x.time || x.t);
        const { macd, signal, hist } = computeMACD(times, closes);
        macdSeries.setData(macd);
        signalSeries.setData(signal);
        histogramSeries.setData(hist.map(p => ({ ...p, color: p.value >= 0 ? '#10b981' : '#ef4444' })));
        dataRefs.current.macd = macd.map((p, i) => ({ time: p.time, macd: p.value, signal: signal[i]?.value, histogram: hist[i]?.value }));
      }
    } catch (_) {}
  };

  const setupVolumeChart = () => {
    const chart = initializeChart('volume', chartRefs.volume);
    if (!chart) return;

    const volumeSeries = chart.addHistogramSeries({
      color: '#3b82f6',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '',
      scaleMargins: {
        top: 0.8,
        bottom: 0,
      },
    });
    seriesRefs.current.volume = volumeSeries;

    try {
      const chartData = stockData?.history?.data;
      if (Array.isArray(chartData) && chartData.length > 0) {
        const volData = chartData.map(item => ({
          time: item.date || item.time || item.t,
          value: Number(item.volume) || Number(item.v) || 0,
          color: (Number(item.close) || Number(item.c) || 0) >= (Number(item.open) || Number(item.o) || 0) ? '#10b981' : '#ef4444'
        }));
        volumeSeries.setData(volData);
        dataRefs.current.volume = volData;
      }
    } catch (_) {}
  };

  // --- Technical computations using history closes ---
  const computeRSI = (times, closes, period = 14) => {
    if (!Array.isArray(closes) || closes.length < period + 1) return [];
    const rsi = [];
    let gains = 0;
    let losses = 0;
    for (let i = 1; i <= period; i++) {
      const change = closes[i] - closes[i - 1];
      if (change >= 0) gains += change; else losses -= change;
    }
    let avgGain = gains / period;
    let avgLoss = losses / period;
    for (let i = period + 1; i < closes.length; i++) {
      const change = closes[i] - closes[i - 1];
      const gain = Math.max(change, 0);
      const loss = Math.max(-change, 0);
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / (avgLoss || 1e-10);
      const value = 100 - 100 / (1 + rs);
      rsi.push({ time: times[i], value });
    }
    return rsi;
  };

  const computeEMA = (values, period) => {
    const ema = [];
    if (values.length < period) return ema;
    const k = 2 / (period + 1);
    // seed with SMA
    let sum = 0;
    for (let i = 0; i < period; i++) sum += values[i];
    let prev = sum / period;
    ema.push(prev);
    for (let i = period; i < values.length; i++) {
      prev = values[i] * k + prev * (1 - k);
      ema.push(prev);
    }
    return ema;
  };

  const computeMACD = (times, closes) => {
    const ema12 = computeEMA(closes, 12);
    const ema26 = computeEMA(closes, 26);
    const macdRaw = [];
    // Align ema12 and ema26 to same index from end
    const start = Math.max(12, 26);
    for (let i = start - 1; i < closes.length; i++) {
      const idx12 = i - (12 - 1);
      const idx26 = i - (26 - 1);
      const macdVal = (ema12[idx12] ?? null) - (ema26[idx26] ?? null);
      macdRaw.push({ time: times[i], value: macdVal });
    }
    const macdValues = macdRaw.map(p => p.value);
    const signalVals = computeEMA(macdValues, 9);
    const signal = [];
    const hist = [];
    // signalVals is shorter; align from the end of macdRaw
    const offset = macdRaw.length - signalVals.length;
    for (let i = 0; i < signalVals.length; i++) {
      const time = macdRaw[i + offset].time;
      const macdV = macdRaw[i + offset].value;
      const sigV = signalVals[i];
      signal.push({ time, value: sigV });
      hist.push({ time, value: macdV - sigV });
    }
    // Trim macd to match signal length for consistent plotting
    const macd = macdRaw.slice(offset);
    return { macd, signal, hist };
  };

  // Live updates for RSI when history changes
  useEffect(() => {
    try {
      const rsiSeries = seriesRefs.current.rsi;
      const chartData = stockData?.history?.data;
      
      console.log('RSI useEffect - rsiSeries:', !!rsiSeries, 'chartData:', chartData);
      
      if (!rsiSeries || !Array.isArray(chartData) || chartData.length < 20) {
        console.warn('RSI useEffect - Missing data:', { rsiSeries: !!rsiSeries, chartDataLength: chartData?.length });
        return;
      }
      
      const closes = chartData.map(x => Number(x.close) || Number(x.c) || 0);
      const times = chartData.map(x => x.date || x.time || x.t);
      
      console.log('RSI useEffect - closes sample:', closes.slice(0, 5), 'times sample:', times.slice(0, 5));
      
      const rsiPoints = computeRSI(times, closes, 14);
      console.log('RSI useEffect - RSI points:', rsiPoints.slice(0, 5));
      
      rsiSeries.setData(rsiPoints);
      dataRefs.current.rsi = rsiPoints;
    } catch (error) {
      console.error('RSI useEffect error:', error);
    }
  }, [stockData?.history?.data]);

  // Live updates for MACD when history changes
  useEffect(() => {
    try {
      const macdSeries = seriesRefs.current.macd;
      const signalSeries = seriesRefs.current.macdSignal;
      const histSeries = seriesRefs.current.macdHist;
      const chartData = stockData?.history?.data;
      
      console.log('MACD useEffect - series:', { macdSeries: !!macdSeries, signalSeries: !!signalSeries, histSeries: !!histSeries }, 'chartData:', chartData);
      
      if (!macdSeries || !signalSeries || !histSeries || !Array.isArray(chartData) || chartData.length < 35) {
        console.warn('MACD useEffect - Missing data:', { macdSeries: !!macdSeries, signalSeries: !!signalSeries, histSeries: !!histSeries, chartDataLength: chartData?.length });
        return;
      }
      
      const closes = chartData.map(x => Number(x.close) || Number(x.c) || 0);
      const times = chartData.map(x => x.date || x.time || x.t);
      
      console.log('MACD useEffect - closes sample:', closes.slice(0, 5), 'times sample:', times.slice(0, 5));
      
      const { macd, signal, hist } = computeMACD(times, closes);
      console.log('MACD useEffect - MACD data:', { macdLength: macd.length, signalLength: signal.length, histLength: hist.length });
      
      macdSeries.setData(macd);
      signalSeries.setData(signal);
      histSeries.setData(hist.map(p => ({ ...p, color: p.value >= 0 ? '#10b981' : '#ef4444' })));
      dataRefs.current.macd = macd.map((p, i) => ({ time: p.time, macd: p.value, signal: signal[i]?.value, histogram: hist[i]?.value }));
    } catch (error) {
      console.error('MACD useEffect error:', error);
    }
  }, [stockData?.history?.data]);

  // Live updates for Volume when history changes
  useEffect(() => {
    try {
      const volSeries = seriesRefs.current.volume;
      const chartData = stockData?.history?.data;
      
      console.log('Volume useEffect - volSeries:', !!volSeries, 'chartData:', chartData);
      
      if (!volSeries || !Array.isArray(chartData) || chartData.length === 0) {
        console.warn('Volume useEffect - Missing data:', { volSeries: !!volSeries, chartDataLength: chartData?.length });
        return;
      }
      
      const volData = chartData.map(item => ({
        time: item.date || item.time || item.t,
        value: Number(item.volume) || Number(item.v) || 0,
        color: (Number(item.close) || Number(item.c) || 0) >= (Number(item.open) || Number(item.o) || 0) ? '#10b981' : '#ef4444'
      }));
      
      console.log('Volume useEffect - volume data sample:', volData.slice(0, 5));
      
      volSeries.setData(volData);
      dataRefs.current.volume = volData;
    } catch (error) {
      console.error('Volume useEffect error:', error);
    }
  }, [stockData?.history?.data]);

  const toggleChart = (chartType) => {
    setVisibleCharts(prev => {
      const wasVisible = prev[chartType];
      const next = { ...prev, [chartType]: !prev[chartType] };
      if (wasVisible) {
        try {
          const chart = chartInstances.current[chartType];
          if (chart && typeof chart.remove === 'function') chart.remove();
        } catch (_) {}
        delete chartInstances.current[chartType];
        delete seriesRefs.current[chartType];
        delete lastPriceLineRefs.current[chartType];
        try {
          const el = chartRefs[chartType]?.current;
          if (el) el.innerHTML = '';
        } catch (_) {}
      }
      return next;
    });
  };

  // Per-chart lifecycle: init when visible, remove on hide/unmount
  useEffect(() => {
    if (visibleCharts.price) setupPriceChart();
    return () => {
      try {
        const chart = chartInstances.current.price;
        if (chart && typeof chart.remove === 'function') chart.remove();
      } catch (_) {}
      delete chartInstances.current.price;
      delete seriesRefs.current.price;
      delete lastPriceLineRefs.current.price;
    };
  }, [visibleCharts.price, symbol]);

  useEffect(() => {
    if (visibleCharts.rsi) setupRSIChart();
    return () => {
      try {
        const chart = chartInstances.current.rsi;
        if (chart && typeof chart.remove === 'function') chart.remove();
      } catch (_) {}
      delete chartInstances.current.rsi;
      delete seriesRefs.current.rsi;
      delete lastPriceLineRefs.current.rsi;
    };
  }, [visibleCharts.rsi, symbol]);

  useEffect(() => {
    if (visibleCharts.macd) setupMACDChart();
    return () => {
      try {
        const chart = chartInstances.current.macd;
        if (chart && typeof chart.remove === 'function') chart.remove();
      } catch (_) {}
      delete chartInstances.current.macd;
      delete seriesRefs.current.macd;
      delete lastPriceLineRefs.current.macd;
    };
  }, [visibleCharts.macd, symbol]);

  useEffect(() => {
    if (visibleCharts.volume) setupVolumeChart();
    return () => {
      try {
        const chart = chartInstances.current.volume;
        if (chart && typeof chart.remove === 'function') chart.remove();
      } catch (_) {}
      delete chartInstances.current.volume;
      delete seriesRefs.current.volume;
      delete lastPriceLineRefs.current.volume;
    };
  }, [visibleCharts.volume, symbol]);

  // Detect symbol changes and set loading state
  useEffect(() => {
    if (symbol !== currentSymbol) {
      console.log('Symbol changed from', currentSymbol, 'to', symbol);
      setIsSymbolLoading(true);
      setCurrentSymbol(symbol);
      
      // Clear existing data and charts when symbol changes
      Object.keys(dataRefs.current).forEach(key => {
        dataRefs.current[key] = [];
      });
      
      // Clear existing series data
      Object.keys(seriesRefs.current).forEach(key => {
        const series = seriesRefs.current[key];
        if (series && typeof series.setData === 'function') {
          try {
            series.setData([]);
          } catch (error) {
            console.warn('Error clearing series data:', error);
          }
        }
      });
    }
  }, [symbol, currentSymbol]);

  // Reset loading state when new data arrives
  useEffect(() => {
    if (stockData?.history?.data && Array.isArray(stockData.history.data) && stockData.history.data.length > 0) {
      if (isSymbolLoading) {
        console.log('New data arrived, clearing loading state');
        setIsSymbolLoading(false);
      }
    }
  }, [stockData?.history?.data, isSymbolLoading]);

  // Global resize listener adjusts any active chart widths
  useEffect(() => {
    const handleResize = () => {
      Object.keys(chartInstances.current).forEach(ct => {
        const chart = chartInstances.current[ct];
        const container = chartRefs[ct]?.current;
        if (chart && container) chart.applyOptions({ width: container.clientWidth });
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for marker clearing events
  useEffect(() => {
    const handleClearMarkers = () => {
      Object.keys(seriesRefs.current).forEach(chartType => {
        const series = seriesRefs.current[chartType];
        if (series && typeof series.setMarkers === 'function') {
          try {
            series.setMarkers([]);
          } catch (_) {}
        }
        // Clear price lines
        if (lastPriceLineRefs.current[chartType] && series && typeof series.removePriceLine === 'function') {
          try {
            series.removePriceLine(lastPriceLineRefs.current[chartType]);
          } catch (_) {}
        }
        delete lastPriceLineRefs.current[chartType];
      });
    };
    window.addEventListener('clearChartMarkers', handleClearMarkers);
    return () => window.removeEventListener('clearChartMarkers', handleClearMarkers);
  }, []);

  return (
    <div className="technical-indicators">
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .toggle-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            background-color: #f3f4f6;
            color: #9ca3af;
          }
          
          .toggle-btn:disabled:hover {
            background-color: #f3f4f6;
            color: #9ca3af;
          }
        `}
      </style>
             <div className="indicators-header">
         <h3>
           Technical Indicators for {symbol}
           {isSymbolLoading && (
             <span style={{
               marginLeft: '8px',
               fontSize: '14px',
               color: '#6b7280',
               fontWeight: 'normal'
             }}>
               (Loading...)
             </span>
           )}
         </h3>
                  <div className="toggle-controls">
           <button 
             className={`toggle-btn ${visibleCharts.price ? 'active' : ''}`}
             onClick={() => toggleChart('price')}
             disabled={isSymbolLoading}
           >
             Price & MA
           </button>
           <button 
             className={`toggle-btn ${visibleCharts.rsi ? 'active' : ''}`}
             onClick={() => toggleChart('rsi')}
             disabled={isSymbolLoading}
           >
             RSI
           </button>
           <button 
             className={`toggle-btn ${visibleCharts.macd ? 'active' : ''}`}
             onClick={() => toggleChart('macd')}
             disabled={isSymbolLoading}
           >
             MACD
           </button>
           <button 
             className={`toggle-btn ${visibleCharts.volume ? 'active' : ''}`}
             onClick={() => toggleChart('volume')}
             disabled={isSymbolLoading}
           >
             Volume
           </button>
         </div>
      </div>

      <div className="charts-container">
                 {visibleCharts.price && (
           <div className="chart-wrapper" style={{ display: 'flex', gap: 12 }}>
             <div style={{ flex: 1, minWidth: 0, width: showClickReset ? 'calc(100% - 312px)' : '100%', position: 'relative' }}>
               <div className="chart-title">Price Chart with Moving Averages</div>
               <div ref={chartRefs.price} className="chart-container" />
               {isSymbolLoading && <LoadingOverlay message="Loading price data..." />}
             </div>
            {showClickReset && (
              <AnalysisCard
                title="Analysis for Price + MA"
                height={300}
                                 content={(() => {
                   if (isSymbolLoading) return 'Loading new symbol data...';
                   const info = analysis?.price;
                   if (!info || !info.time) return 'Click on the price chart to see analysis.';
                  const when = formatDate(info.time);
                  const fmt = (v) => (v == null ? 'N/A' : `${Number(v).toFixed(2)}`);
                  const lines = [
                    `Date: ${when}`,
                    `Clicked Price: ${fmt(info.price)}`,
                  ];
                  if (info.ohlc) {
                    lines.push(
                      `Open:  ${fmt(info.ohlc.open)}`,
                      `High:  ${fmt(info.ohlc.high)}`,
                      `Low:   ${fmt(info.ohlc.low)}`,
                      `Close: ${fmt(info.ohlc.close)}`,
                    );
                  }
                  
                  // Add historical OHLCV data if available
                  if (info.history && info.history.length > 0) {
                    lines.push('', 'Historical OHLCV Data:');
                    lines.push('Date | O | H | L | C | Volume');
                    lines.push('─'.repeat(50));
                    
                    // Show last 20 bars for compact display
                    const recentHistory = info.history.slice(-20);
                    recentHistory.forEach(item => {
                      const volume = item.volume >= 1000000 ? `${(item.volume / 1000000).toFixed(1)}M` : 
                                   item.volume >= 1000 ? `${(item.volume / 1000).toFixed(1)}K` : 
                                   item.volume.toString();
                      lines.push(
                        `${item.time} | ${item.open.toFixed(2)} | ${item.high.toFixed(2)} | ${item.low.toFixed(2)} | ${item.close.toFixed(2)} | ${volume}`
                      );
                    });
                    
                    if (info.history.length > 20) {
                      lines.push(`... and ${info.history.length - 20} more bars`);
                    }
                  }
                  
                  return lines.join('\n');
                })()}
              />
            )}
          </div>
        )}

                 {visibleCharts.rsi && (
           <div className="chart-wrapper" style={{ display: 'flex', gap: 12 }}>
             <div style={{ flex: 1, minWidth: 0, width: showClickReset ? 'calc(100% - 312px)' : '100%', position: 'relative' }}>
               <div className="chart-title">RSI (14)</div>
               <div ref={chartRefs.rsi} className="chart-container" />
               {isSymbolLoading && <LoadingOverlay message="Loading RSI data..." />}
             </div>
            {showClickReset && (
                             <AnalysisCard
                 title="Analysis for RSI"
                 height={300}
                 content={(() => {
                   if (isSymbolLoading) return 'Loading new symbol data...';
                   const info = analysis?.rsi;
                   if (!info || !info.time) return 'Click on the RSI chart to see analysis.';
                   const when = formatDate(info.time);
                   const fmt = (v) => (v == null ? 'N/A' : `${Number(v).toFixed(2)}`);
                   return `Date: ${when}\nRSI Value: ${fmt(info.value)}`;
                 })()}
               />
            )}
          </div>
        )}

                 {visibleCharts.macd && (
           <div className="chart-wrapper" style={{ display: 'flex', gap: 12 }}>
             <div style={{ flex: 1, minWidth: 0, width: showClickReset ? 'calc(100% - 312px)' : '100%', position: 'relative' }}>
               <div className="chart-title">MACD (12,26,9)</div>
               <div ref={chartRefs.macd} className="chart-container" />
               {isSymbolLoading && <LoadingOverlay message="Loading MACD data..." />}
             </div>
            {showClickReset && (
                             <AnalysisCard
                 title="Analysis for MACD"
                 height={300}
                 content={(() => {
                   if (isSymbolLoading) return 'Loading new symbol data...';
                   const info = analysis?.macd;
                   if (!info || !info.time) return 'Click on the MACD chart to see analysis.';
                   const when = formatDate(info.time);
                   const fmt = (v) => (v == null ? 'N/A' : `${Number(v).toFixed(2)}`);
                   const ex = info.extras || {};
                   return `Date: ${when}\nMACD: ${fmt(ex.macd)}\nSignal: ${fmt(ex.signal)}\nHistogram: ${fmt(ex.histogram)}`;
                 })()}
               />
            )}
          </div>
        )}

                 {visibleCharts.volume && (
           <div className="chart-wrapper" style={{ display: 'flex', gap: 12 }}>
             <div style={{ flex: 1, minWidth: 0, width: showClickReset ? 'calc(100% - 312px)' : '100%', position: 'relative' }}>
               <div className="chart-title">Volume</div>
               <div ref={chartRefs.volume} className="chart-container" />
               {isSymbolLoading && <LoadingOverlay message="Loading volume data..." />}
             </div>
            {showClickReset && (
              <AnalysisCard
                title="Analysis for Volume"
                height={300}
                                 content={(() => {
                   if (isSymbolLoading) return 'Loading new symbol data...';
                   const info = analysis?.volume;
                   if (!info || !info.time) return 'Click on the Volume chart to see analysis.';
                  const when = formatDate(info.time);
                  const fmt = (v) => (v == null ? 'N/A' : `${Number(v).toFixed(0)}`);
                  return `Date: ${when}\nVolume: ${fmt(info.value)}`;
                })()}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicalIndicators; 